import { Component, OnInit, inject } from '@angular/core';
import { FormTemplateService } from 'src/app/Services/Forms/form-template.service';
import { Form_Template, Response_Forms } from 'src/app/Interfaces/Forms/FormTemplate.interface';

@Component({
  selector: 'app-form-reports-cdms',
  templateUrl: './form-reports-cdms.component.html',
  styleUrls: ['./form-reports-cdms.component.scss'],
  standalone: false
})
export class FormReportsCDMSComponent implements OnInit {

  private formService = inject(FormTemplateService);

  // Overview
  public Overview: any = null;
  public IsLoadingOverview: boolean = true;

  // Template selector
  public Templates: Form_Template[] = [];
  public SelectedTemplate: Form_Template | null = null;
  public FieldStats: any[] = [];
  public IsLoadingStats: boolean = false;

  // Charts
  public TrendChartData: any = null;
  public TrendChartOptions: any = null;
  public TopTemplatesChartData: any = null;
  public TopTemplatesChartOptions: any = null;

  ngOnInit(): void {
    this.LoadOverview();
    this.LoadTemplates();
  }

  private LoadOverview(): void {
    this.IsLoadingOverview = true;
    this.formService.GetReportsOverview(30).subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          this.Overview = res.operation;
          this.BuildTrendChart();
          this.BuildTopTemplatesChart();
        }
        this.IsLoadingOverview = false;
      },
      error: () => { this.IsLoadingOverview = false; }
    });
  }

  private LoadTemplates(): void {
    this.formService.GetAllFormTemplates(2).subscribe({
      next: (res: Response_Forms) => {
        if (res.success) this.Templates = res.operation;
      }
    });
  }

  public OnTemplateSelect(event: any): void {
    if (!event.value) {
      this.FieldStats = [];
      return;
    }
    this.LoadFieldStats(event.value.ID);
  }

  private LoadFieldStats(templateId: number): void {
    this.IsLoadingStats = true;
    this.formService.GetFieldStatistics(templateId).subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          this.FieldStats = res.operation;
          this.FieldStats.forEach(f => this.BuildFieldChart(f));
        }
        this.IsLoadingStats = false;
      },
      error: () => { this.IsLoadingStats = false; }
    });
  }

  private BuildTrendChart(): void {
    if (!this.Overview?.trend?.length) return;
    this.TrendChartData = {
      labels: this.Overview.trend.map((t: any) => new Date(t.Fecha).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })),
      datasets: [{
        label: 'Submissions',
        data: this.Overview.trend.map((t: any) => t.Total),
        fill: true,
        backgroundColor: 'rgba(32, 168, 225, 0.1)',
        borderColor: '#20a8e1',
        tension: 0.4
      }]
    };
    this.TrendChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
    };
  }

  private BuildTopTemplatesChart(): void {
    if (!this.Overview?.topTemplates?.length) return;
    this.TopTemplatesChartData = {
      labels: this.Overview.topTemplates.map((t: any) => t.Name.length > 25 ? t.Name.substring(0, 25) + '...' : t.Name),
      datasets: [{
        data: this.Overview.topTemplates.map((t: any) => t.Total),
        backgroundColor: ['#20a8e1', '#7c3aed', '#f59e0b', '#22c55e', '#fc6161']
      }]
    };
    this.TopTemplatesChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } }
    };
  }

  private BuildFieldChart(field: any): void {
    if (!field.stats || !field.stats.values?.length) return;

    if (field.fieldType === 'boolean') {
      field.chartData = {
        labels: ['Si', 'No'],
        datasets: [{
          data: [field.stats.yesCount || 0, field.stats.noCount || 0],
          backgroundColor: ['#22c55e', '#fc6161']
        }]
      };
      field.chartType = 'doughnut';
      field.chartOptions = { plugins: { legend: { position: 'bottom' } } };
    } else if (field.fieldType === 'select' && field.stats.values?.length > 0) {
      field.chartData = {
        labels: field.stats.values.map((v: any) => {
          const val = String(v.Value);
          return val.length > 20 ? val.substring(0, 20) + '...' : val;
        }),
        datasets: [{
          label: 'Respuestas',
          data: field.stats.values.map((v: any) => v.Count),
          backgroundColor: '#20a8e1'
        }]
      };
      field.chartType = 'bar';
      field.chartOptions = {
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } } }
      };
    } else if (field.fieldType === 'multi_select' && field.stats.optionDistribution?.length > 0) {
      field.chartData = {
        labels: field.stats.optionDistribution.map((o: any) => {
          return o.option.length > 20 ? o.option.substring(0, 20) + '...' : o.option;
        }),
        datasets: [{
          label: 'Selecciones',
          data: field.stats.optionDistribution.map((o: any) => o.count),
          backgroundColor: '#7c3aed'
        }]
      };
      field.chartType = 'bar';
      field.chartOptions = {
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } } }
      };
    }
  }

  public GetFieldTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'text_short': 'Texto corto', 'text_long': 'Texto largo',
      'number': 'Numero', 'date': 'Fecha',
      'select': 'Seleccion', 'multi_select': 'Seleccion multiple',
      'boolean': 'Si/No', 'signature': 'Firma',
      'table': 'Tabla', 'image': 'Imagen', 'file': 'Archivo'
    };
    return labels[type] || type;
  }

  public GetFieldIcon(type: string): string {
    const icons: { [key: string]: string } = {
      'text_short': 'fa-solid fa-font', 'text_long': 'fa-solid fa-align-left',
      'number': 'fa-solid fa-hashtag', 'date': 'fa-solid fa-calendar',
      'select': 'fa-solid fa-caret-down', 'multi_select': 'fa-solid fa-list-check',
      'boolean': 'fa-solid fa-toggle-on', 'signature': 'fa-solid fa-signature',
      'table': 'fa-solid fa-table-cells', 'image': 'fa-solid fa-image',
      'file': 'fa-solid fa-paperclip'
    };
    return icons[type] || 'fa-solid fa-question';
  }
}
